<template>
  <div
    ref="templateCardRef"
    :class="['app-card__root', seq === 1 && 'template-1st', isGuideActive && 'guide-active']"
    @click="createTemplateApp"
    @mouseenter="() => setPopover(true)"
  >
    <div class="app-card__main">
      <div ref="guideRef" class="guide-ref"></div>
      <div class="app-img">
        <lc-img :imgUrl="props.appData.icon"></lc-img>
      </div>
      <div class="app-info">
        <div class="header">
          <span class="app-name">{{ props.appData.name }}</span>
        </div>
        <div class="app-desc">{{ props.appData.brief_intro }}</div>
      </div>
    </div>
    <div class="app-card__footer">
      <div v-for="(label, index) in props.appData.labels" :key="label + '_' + index" class="app-label">
        {{ label }}
      </div>
    </div>
    <el-popover v-if="popover" placement="top" effect="light" :width="50" popperClass="view-app-detail-pop">
      <template #reference>
        <span class="app-card__info" @click.stop="onAppClick">
          <IconMyIconTemplateInfo class="template-info"></IconMyIconTemplateInfo>
        </span>
      </template>
      <template #default>查看详情</template>
    </el-popover>
  </div>

  <template-guide
    v-if="isShowTemplateGuide && enableTemGuide"
    :popRef="guideRef"
    @on-destroy-tem-guide="enableTemGuide = false"
  ></template-guide>

  <!-- 工作区下应用创建 -->
  <workspace-app-create
    v-if="isShowWorkspaceAppCreate"
    :platformId="appData.platform_id"
    :tplId="appData.tpl_id"
    :tplName="appData.name"
    :workspaceId="workspaceId"
    :appIcon="props.appData.icon"
    @on-workspace-app-create-dialog-closed="onWorkspaceAppCreateDialogClosed"
  ></workspace-app-create>

  <!-- 工作区下的应用列表弹窗 -->
  <workspace-app
    v-if="isAppCreatedBefore && isShowAppList"
    :platformId="appData.platform_id"
    :tplId="appData.tpl_id"
    :tplName="appData.name"
    :workspaceId="workspaceId"
    @on-workspace-app-dialog-closed="onWorkspaceAppDialogClosed"
  ></workspace-app>
</template>

<script setup lang="ts">
import LcImg from '@/features/lc-img/lc-img.vue';
import { RespTplApp } from '@vas2t-lowcode/api';
import { computed, onMounted, PropType, ref, nextTick, defineAsyncComponent } from 'vue';
import { LOCAL_STORAGE_ID, LOCAL_STORAGE_TEMPLATE_GUIDE } from '@/features/guide/template-guide.constants';
import { getLocalStoragePrivate } from '@/utils/storage-tools';
import { getGuideEmitter } from '../guide/guide.emitter';
import TemplateGuide from '@/features/guide/template-guide.vue';
import { usePTeamStore } from '@/store';
import { useIntersectionObserver } from '@vueuse/core';
import { dwOnSingleTemplateShow } from './app-list.dw';
import { useWTemplateAppCreate } from './use-w-app-create';

const props = defineProps({
  appData: {
    type: Object as PropType<RespTplApp>,
    default() {
      return {};
    },
  },
  seq: {
    type: Number,
    require: true,
    default: 0,
  },
});

const emit = defineEmits<{
  (e: 'on-app-click', data: RespTplApp): void;
}>();

const onAppClick = () => {
  emit('on-app-click', props.appData);
};

// #region 应用创建
const WorkspaceAppCreate = defineAsyncComponent(() => import('@/features/workspace-app/workspace-app-create.vue'));
const WorkspaceApp = defineAsyncComponent(() => import('@/features/workspace-app/workspace-app.vue'));

const {
  isShowWorkspaceAppCreate,
  createTemplateApp,
  onWorkspaceAppCreateDialogClosed,
  isAppCreatedBefore,
  onWorkspaceAppDialogClosed,
  workspaceId,
  isShowAppList,
} = useWTemplateAppCreate(props.appData);
// #endregion

// #region guide指引
const isGuideActive = ref(false);
const { isShowWorkbench, isInitStaticsData } = usePTeamStore();
const isShowTemplateGuide = computed(() => {
  // 无工作区和第一个模板才显示模板指引
  return isInitStaticsData && !isShowWorkbench && props.seq === 0;
});
const checkIsShowTemplateRef = () => {
  window.setTimeout(() => {
    // 第一个模板类型显示
    if (isShowTemplateGuide.value) {
      const isShowed = !!getLocalStoragePrivate({
        id: LOCAL_STORAGE_ID,
        key: LOCAL_STORAGE_TEMPLATE_GUIDE,
      })?.isShowed;

      isGuideActive.value = !isShowed;

      if (!isShowed) {
        onHideGuideEmitter();
        // 显示模板指引
        getGuideEmitter().emit('show-template-guide');
        // 显示帮助
        autoShowHelpGuide();
      }
    }
  }, 500);
};

const onHideGuideEmitter = () => {
  getGuideEmitter().on('hide-template-guide', () => {
    // 取消UI
    isGuideActive.value = false;
  });
};

const popover = ref(false);
const setPopover = (val: boolean) => {
  popover.value = val;
};

const enableTemGuide = ref(true);
/** 显示帮助 */
const autoShowHelpGuide = () => {
  // 9s内不点击则显示帮助
  const clickTimer = window.setTimeout(() => {
    getGuideEmitter().emit('hide-template-guide');
    nextTick(() => {
      getGuideEmitter().emit('show-help-qrcode');
    });
  }, 9000);

  const clickHandler = () => {
    clickTimer && window.clearTimeout(clickTimer);
    document.removeEventListener('click', clickHandler);
  };

  document.addEventListener('click', clickHandler);
};

const guideRef = ref<HTMLDivElement>();

// #region 埋点相关逻辑
const templateCardRef = ref<HTMLDivElement | null>(null);
const { stop } = useIntersectionObserver(templateCardRef, ([{ isIntersecting }]) => {
  if (isIntersecting) {
    // 触发埋点并终止监听
    const { tpl_id, name, category_name } = props.appData;
    dwOnSingleTemplateShow(tpl_id || '', name || '', category_name || '');
    stop();
  }
});
// #endregion

onMounted(() => {
  checkIsShowTemplateRef();
});
// #endregion
</script>

<style lang="scss" scoped>
.app-card {
  &__root {
    cursor: pointer;
    box-sizing: border-box;
    // width: 262px;
    width: 100%;
    // height: 139px;
    background: var(--lowcode-background-01);
    border-radius: 8px;
    border: 1px solid var(--lowcode-ctl-border-04);
    position: relative;
    padding: 20px 20px 13px 20px;
    transition: box-shadow ease-in-out 0.25s;

    content-visibility: auto;
    contain-intrinsic-size: 139px;
    // margin-right: 16px;
    // margin-bottom: 16px;
    .app {
      &-desc {
        box-sizing: border-box;
        font-size: 12px;
        color: var(--lowcode-text-06);
        line-height: 22px;
        height: 44px;
        @extend %lowcode-two-line;
      }
      &-use {
        display: none;
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        // width: 260px;
        height: 44px;
        background: var(--lowcode-custom-bg-29);

        .btn-use {
          width: 80px;
          height: 26px;
          min-height: 26px;
          position: absolute;
          right: 20px;
          top: 50%;
          transform: translateY(-50%);
        }
      }
    }

    &:hover,
    &.guide-active {
      box-shadow: 0px 4px 12px 0px var(--lowcode-custom-border-11);
      .app-use {
        display: block;
      }
    }

    &:hover .app-card__info {
      opacity: 1;
    }
  }

  &__main {
    box-sizing: border-box;
    // overflow: hidden;
    display: flex;
    // height: 48px;
    position: relative;
    // padding-bottom: 8px;
    border-bottom: 1px solid var(--lowcode-custom-border-07);

    .guide-ref {
      position: absolute;
      right: -20px;
      top: 11px;
    }

    .app {
      &-img {
        flex: none;
        display: inline-block;
        width: 36px;
        height: 36px;
        border-radius: 6px;
      }
      &-info {
        box-sizing: border-box;
        flex: 1;
        box-sizing: border-box;
        display: inline-block;
        padding-left: 10px;
        width: 184px;
        // height: 44px;
        transform: translateY(-6px);

        .header {
          display: flex;
          flex-wrap: nowrap;
          width: 100%;
          overflow: hidden;
        }
      }
      &-name {
        height: 22px;
        font-size: 14px;
        color: var(--lowcode-text-02);
        line-height: 22px;
        flex-shrink: 1;
        @extend %lowcode-single-line;
      }
      // &-install-info {
      //   height: 22px;
      //   font-size: 12px;
      //   color: var(--lowcode-text-05);
      //   line-height: 22px;
      // }
    }
  }

  &__footer {
    box-sizing: border-box;
    display: flex;
    overflow: hidden;
    height: 48px;
    padding: 14px 0;
    flex-wrap: wrap;
    row-gap: 20px;
  }

  &__info {
    position: absolute;
    width: 16px;
    height: 16px;
    right: 10px;
    top: 10px;
    padding: 4px;
    border-radius: 4px;
    opacity: 0;

    &:hover {
      background-color: var(--lowcode-loading-01);
    }
    &::before {
      content: '';
      position: absolute;
      top: -4px;
      right: -4px;
      bottom: -4px;
      left: -4px;
    }
  }
}

.app-label {
  flex: none;
  max-width: 118px;
  box-sizing: border-box;
  padding: 0 6px;
  min-width: 36px;
  height: 20px;
  background: var(--lowcode-custom-bg-103);
  border-radius: 4px;
  font-size: 12px;
  color: var(--lowcode-text-04);
  line-height: 20px;
  text-align: center;
  @extend %lowcode-single-line;

  &:not(:first-child) {
    margin-left: 8px;
  }
}

// @media (max-width: 890px) {
//   .app-card {
//     &-root {
//       width: 270px;
//     }
//   }
// }
</style>

<style>
.el-popover.el-popover.view-app-detail-pop {
  min-width: 20px;
  padding: 8px;
}
</style>
